import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import type { EChartsOption, SeriesOption } from 'echarts';
import { SingleQuestionResponsesComponent } from '../single-question-responses/single-question-responses.component';
import { MatDialog } from '@angular/material/dialog';
import { SurveyService } from '../../../services/surveys.service';
import { TranslateService } from '@ngx-translate/core';
import mermaid, { MermaidConfig } from 'mermaid';

interface MetricConfig {
  labelKey: string;
  color: string;
  getValue: (q: any) => number;
}

@Component({
  selector: 'app-question-history-dashboard',
  templateUrl: './question-history-dashboard.component.html',
  styleUrls: ['./question-history-dashboard.component.scss'],
  standalone: false
})
export class QuestionHistoryDashboardComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() questionHistory: any;
  // accordionOpened non serve più per l'apertura, ma lo lasciamo per compatibilità inputs
  @Input() accordionOpened = true;
  @ViewChild('mermaid', { static: false }) mermaidDiv!: ElementRef;

  loading = false;

  chartOptions: EChartsOption = {};
  currentChartType: 'bar' | 'line' | 'heatmap' = 'bar';
  availableChartTypes: ('bar' | 'line' | 'heatmap')[] = [];

  mermaidDefinition: string | null = null;
  filteredContent: string = '';
  wordcloudLoading: boolean = false;
  currentSurvey: any = null;
  surveys: any[] = [];
  surveysToCompare: any[] = [];
  isSmooth = false;

  // Configurazione Mermaid aggiornata per stile curve e colori
  mermaidConfig: MermaidConfig = {
    startOnLoad: false,
    flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
    securityLevel: 'loose',
    theme: 'base',
    themeVariables: {
      primaryColor: '#9169db',
      primaryTextColor: '#fff',
      primaryBorderColor: '#5e4391',
      lineColor: '#9169db',
      secondaryColor: '#f4f0fc',
      tertiaryColor: '#fff'
    }
  };

  constructor(
    private dialog: MatDialog,
    public surveyService: SurveyService,
    protected translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  get question() {
    return this.questionHistory?.questions ? this.questionHistory.questions[0] : null;
  }

  getIconByType(): string {
    const type = this.question?.typology;

    if (this.surveyService.isConsensusQuestion(type)) { return 'handshake'; }
    if (this.surveyService.isQuantitativeQuestion(type)) { return 'calculate'; }
    if (this.surveyService.isOpenedQuestion(type)) { return 'subject'; }
    if (this.surveyService.isMultipleChoiceQuestion(type)) { return 'radio_button_checked'; }
    if (this.surveyService.isCheckboxQuestion(type)) { return 'check_box'; }
    if (this.surveyService.isGridQuestion(type) || this.surveyService.isClassificationQuestion(type)) { return 'grid_on'; }

    return 'poll';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['questionHistory'] && !changes['questionHistory'].firstChange) {
      this.refreshDashboardData();
    }
  }

  ngOnInit(): void {
    this.refreshDashboardData();
  }

  refreshDashboardData(): void {
    if (this.questionHistory?.questions?.length) {
      this.surveys = this.questionHistory.questions.map((q: any) => ({
        id: q.survey_id || q.id,
        version_number: q.survey_version_number || q.major_version?.toString(),
        major_version: q.major_version || 1,
        minor_version: q.minor_version || 0,
        is_draft: q.status === 'DRAFT',
        survey_question: q
      })).sort((a: any, b: any) =>
        a.major_version !== b.major_version
          ? a.major_version - b.major_version
          : a.minor_version - b.minor_version
      );

      this.currentSurvey = this.surveys[this.surveys.length - 1]; // Start with latest session
      if (this.surveyService.isOpenedQuestion(this.question?.typology)) {
        this.wordcloudLoading = true;
      }
      this.updateFilteredContent();
      this.initChartCapabilities();

      if (this.availableChartTypes.length > 0) {
        this.buildChartOptions();
      }
      this.renderMermaidDiagram();
    }
  }

  ngAfterViewInit(): void {
    this.renderMermaidDiagram();
  }

  private initChartCapabilities() {
    const typology = this.question?.typology;
    this.availableChartTypes = [];

    if (this.surveyService.isConsensusQuestion(typology)) {
      this.availableChartTypes = ['bar', 'line'];
    }
    else if (this.surveyService.isQuantitativeQuestion(typology)) {
      this.availableChartTypes = ['bar', 'line'];
    }
    else if (this.surveyService.isMultipleChoiceQuestion(typology) || this.surveyService.isCheckboxQuestion(typology)) {
      this.availableChartTypes = ['bar', 'line'];
    }
    else if (this.surveyService.isGridQuestion(typology) || this.surveyService.isClassificationQuestion(typology)) {
      this.availableChartTypes = ['heatmap'];
    }
    else if (this.surveyService.isOpenedQuestion(typology)) {
      this.availableChartTypes = ['line', 'bar'];
    }
    else {
      this.availableChartTypes = [];
    }

    if (this.availableChartTypes.length > 0) {
      // Default to line chart if multiple sessions, otherwise bar
      if (this.surveys.length > 1 && this.availableChartTypes.includes('line')) {
        this.currentChartType = 'line';
      } else if (this.availableChartTypes.includes('bar')) {
        this.currentChartType = 'bar';
      } else {
        this.currentChartType = this.availableChartTypes[0];
      }
    }
  }

  /**
   * Palette Aggiornata: #9169db (Primary)
   */
  private getMetricsConfig(): MetricConfig[] {
    const typology = this.question?.typology;

    // 1. CONSENSUS
    if (this.surveyService.isConsensusQuestion(typology)) {
      return [
        {
          labelKey: 'generic.certainty-degree',
          color: '#9169db', // Primary Purple
          getValue: (q) => q.results?.certainty_degree ?? 0
        },
        {
          labelKey: 'generic.contradiction-degree',
          color: '#26a69a', // Teal (Complementare per contrasto)
          getValue: (q) => q.results?.contradiction_degree ?? 0
        },
        {
          labelKey: 'components.responses.consensus.agree',
          color: '#ffca28', // Giallo ambrato (Rimane per "attenzione")
          getValue: (q) => {
            if (q.results && Array.isArray(q.responses) && q.responses.length > 0) {
              const agreeCount = q.responses.filter((r: any) => r?.answer === 'AGREE').length;
              return (agreeCount / q.responses.length) * 100;
            }
            return q.results?.a_ratio ?? 0;
          }
        }
      ];
    }

    // 2. QUANTITATIVO
    if (this.surveyService.isQuantitativeQuestion(typology)) {
      return [
        { labelKey: 'generic.average', color: '#9169db', getValue: (q) => q.results?.mean ?? 0 }, // Purple
        { labelKey: 'generic.geometric-average', color: '#b39ddb', getValue: (q) => q.results?.geometric_mean ?? 0 }, // Lighter Purple
        { labelKey: 'generic.median', color: '#ec407a', getValue: (q) => q.results?.median ?? 0 }, // Pink
        { labelKey: 'generic.variance', color: '#ffb74d', getValue: (q) => q.results?.variance ?? 0 } // Orange
      ];
    }

    // 3. MULTIPLE CHOICE / CHECKBOX
    if (this.surveyService.isMultipleChoiceQuestion(typology) || this.surveyService.isCheckboxQuestion(typology)) {
      const questions = this.questionHistory?.questions || [];
      const uniqueChoices = new Set<string>();

      questions.forEach((q: any) => {
        q.choices?.forEach((c: any) => {
          if (c.title) uniqueChoices.add(c.title);
        });
      });

      const colors = ['#9169db', '#26a69a', '#ffca28', '#ec407a', '#ffb74d', '#42a5f5', '#66bb6a', '#ef5350'];
      return Array.from(uniqueChoices).map((title, index) => ({
        labelKey: title,
        color: colors[index % colors.length],
        getValue: (q: any) => {
          const choice = q.choices?.find((c: any) => c.title === title);
          return choice ? (choice.responses_count || 0) : 0;
        }
      }));
    }

    // 4. OPENED (Sentiment)
    if (this.surveyService.isOpenedQuestion(typology)) {
      return [
        {
          labelKey: 'generic.sentiment-score',
          color: '#26a69a',
          getValue: (q: any) => {
            const responses = q.responses || [];
            if (responses.length === 0) return 0;
            const sum = responses.reduce((acc: number, r: any) => acc + (r.customs?.score || 0), 0);
            return (sum / responses.length);
          }
        }
      ];
    }

    return [];
  }

  private buildChartOptions() {
    const typology = this.question?.typology;

    if (this.surveyService.isGridQuestion(typology) || this.surveyService.isClassificationQuestion(typology)) {
      this.buildHeatmapOptions();
      return;
    }

    const metrics = this.getMetricsConfig();
    const questions = this.questionHistory.questions;

    if (!metrics.length || !questions) { return; }

    let revCounter = 1;
    const xLabels = questions.map((q: any, i: number) => {
      let label = `Session ${q?.survey_version_number}`;

      // Sentiment icon for OPENED typology using ECharts Rich Text + Material Icons font
      if (this.surveyService.isOpenedQuestion(typology)) {
        const responses = q.responses || [];
        if (responses.length > 0) {
          const sum = responses.reduce((acc: number, r: any) => acc + (r.customs?.score || 0), 0);
          const avg = sum / responses.length;

          let icon = 'sentiment_neutral';
          let style = 'neu';
          if (avg > 0) { icon = 'sentiment_satisfied_alt'; style = 'pos'; }
          if (avg < 0) { icon = 'sentiment_very_dissatisfied'; style = 'neg'; }

          label += `\n{${style}|${icon}}`;
        }
      }

      const prev = questions[i - 1];
      if (!prev) { return label; }

      if (prev?.final_decision === 'revised') { return label + `\n(REV ${revCounter++})`; }
      if (prev?.final_decision === 'revised_split') { return label + `\n(SPLIT)`; }
      return label;
    });

    const series: SeriesOption[] = metrics.map(metric => {
      const dataValues = questions.map((q: any) => {
        const val = metric.getValue(q);
        if (val == null) return '0';

        const typology = this.question?.typology;
        const isChoice = this.surveyService.isMultipleChoiceQuestion(typology) || this.surveyService.isCheckboxQuestion(typology);

        return isChoice ? Math.round(val).toString() : Number(val).toFixed(2);
      });

      return {
        name: metric.labelKey.includes('.') ? this.translateService.instant(metric.labelKey) : metric.labelKey,
        type: this.currentChartType,
        smooth: this.currentChartType === 'line' ? this.isSmooth : false,
        // Area sfumata con il colore della metrica
        areaStyle: this.currentChartType === 'line' ? {
          opacity: 0.2,
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: metric.color }, { offset: 1, color: 'rgba(255,255,255,0)' }]
          }
        } : undefined,
        barMaxWidth: 40, // Barre leggermente più sottili ed eleganti
        itemStyle: {
          color: metric.color,
          borderRadius: this.currentChartType === 'bar' ? [6, 6, 0, 0] : 4
        },
        data: dataValues
      };
    });

    this.chartOptions = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#eef0f5',
        borderWidth: 1,
        textStyle: { color: '#333' },
        extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;', // Ombra tooltip
        formatter: (params: any) => {
          let html = `<div style="font-weight:700; color: #5e4391; margin-bottom:8px; padding-bottom:4px; border-bottom:1px solid #eee;">
                        ${params[0].axisValueLabel.split('\n')[0]}
                      </div>`;
          params.forEach((p: any) => {
            html += `<div style="display:flex; justify-content:space-between; align-items:center; min-width:140px; margin-bottom:4px;">
                      <div style="display:flex; align-items:center;">
                        <span style="display:inline-block; width:10px; height:10px; border-radius:50%; background-color:${p.color}; margin-right:8px;"></span>
                        <span style="font-size:12px; color:#666;">${p.seriesName}</span>
                      </div>
                      <span style="font-weight:bold; font-size:13px; margin-left:12px;">${p.value}${this.surveyService.isConsensusQuestion(this.question.typology) ? '%' : ''}</span>
                     </div>`;
          });
          return html;
        }
      },
      legend: {
        data: series.map((s: any) => s.name),
        bottom: 0,
        icon: 'roundRect', // Icona legenda più moderna
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { color: '#666' }
      },
      grid: { left: '3%', right: '4%', bottom: '12%', top: '8%', containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: this.currentChartType === 'bar',
        data: xLabels,
        axisLine: { show: false }, // Meno rumore visivo
        axisTick: { show: false },
        axisLabel: {
          color: '#888',
          fontWeight: 'bold',
          rich: {
            pos: { fontFamily: 'Material Icons', color: '#26a69a', fontSize: 22, height: 30, padding: [5, 0] },
            neg: { fontFamily: 'Material Icons', color: '#ef5350', fontSize: 22, height: 30, padding: [5, 0] },
            neu: { fontFamily: 'Material Icons', color: '#9e9e9e', fontSize: 22, height: 30, padding: [5, 0] }
          }
        }
      },
      yAxis: {
        type: 'value',
        minInterval: (this.surveyService.isMultipleChoiceQuestion(this.question.typology) || this.surveyService.isCheckboxQuestion(this.question.typology)) ? 1 : undefined,
        splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } }, // Linee griglia molto leggere
        axisLabel: { color: '#999' }
      },
      series
    };
  }

  setChartType(type: 'line' | 'bar' | 'heatmap') {
    if (this.availableChartTypes.includes(type as any)) {
      this.currentChartType = type as any;
      this.buildChartOptions();
    }
  }

  private buildHeatmapOptions() {
    const question = this.currentSurvey?.survey_question;
    if (!question) return;

    const rows = question.grid_rows || [];
    const cols = question.grid_columns || [];
    const responses = question.responses || [];

    const rowLabels = rows.map((r: any) => r.title);
    const colLabels = cols.map((c: any) => c.title);

    const data: any[] = [];
    let max = 0;

    rows.forEach((row: any, rIdx: number) => {
      cols.forEach((col: any, cIdx: number) => {
        let count = 0;
        responses.forEach((res: any) => {
          if (Array.isArray(res.answer)) {
            if (res.answer.some((a: any) =>
              (a.row == row.id || a.grid_question_row_id == row.id) &&
              (a.column == col.id || a.grid_question_column_id == col.id)
            )) {
              count++;
            }
          }
        });
        data.push([cIdx, rIdx, count]);
        if (count > max) max = count;
      });
    });

    this.chartOptions = {
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          return `<div style="padding: 5px 10px;">
                    <strong style="color: #5e4391;">${rowLabels[params.data[1]]}</strong><br/>
                    <span style="color: #666;">${colLabels[params.data[0]]}</span>:
                    <strong style="font-size: 1.1em;">${params.data[2]}</strong>
                  </div>`;
        }
      },
      grid: { height: '80%', top: '10%', bottom: '15%', containLabel: true },
      xAxis: {
        type: 'category',
        data: colLabels,
        splitArea: { show: true },
        axisLabel: { rotate: 30, color: '#888', fontWeight: 'bold' }
      },
      yAxis: {
        type: 'category',
        data: rowLabels,
        splitArea: { show: true },
        axisLabel: { color: '#888', fontWeight: 'bold' }
      },
      visualMap: {
        min: 0,
        max: max || 1,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '2%',
        inRange: {
          color: ['#fff5f5', '#feb2b2', '#fc8181', '#f56565', '#c53030']
        }
      },
      series: [{
        name: 'Responses',
        type: 'heatmap',
        data: data,
        label: {
          show: true,
          color: '#333'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        }
      }]
    };
  }

  // --- Mermaid Logic ---

  renderMermaidDiagram(): void {
    mermaid.initialize(this.mermaidConfig);
    const el = this.mermaidDiv?.nativeElement;

    if (el) {
      this.mermaidDefinition = this.generateMermaidDiagram();
      const uniqueId = `mermaid_${this.question.id}_${Math.floor(Math.random() * 1000)}`;
      mermaid.render(uniqueId, this.mermaidDefinition).then((result) => {
        el.innerHTML = result.svg;
        // @ts-ignore
        result.bindFunctions?.();

        const svg = el.querySelector('svg');
        if (svg) {
          const nodes = svg.querySelectorAll('.node');
          svg.style.display = 'block';
          svg.style.margin = '0 auto';

          nodes.forEach(node => {
            node.addEventListener('click', () => {
              const rawId = node.id;
              const match = rawId.match(/S_(\d+)/);
              if (match) {
                const surveyId = parseInt(match[1], 10);
                this.onNodeClick(surveyId);
              }
            });
            node.addEventListener('mouseenter', () => { node.style.cursor = 'pointer'; });
            node.addEventListener('mouseleave', () => { node.style.fill = ''; });
          });
        }
      }).catch(err => console.error('Mermaid error:', err));
    }
  }

  onNodeClick(surveyID: number) {
    this.currentSurvey = this.surveys?.find((survey) => surveyID == survey?.id);
    if (this.surveyService.isOpenedQuestion(this.question?.typology)) {
      this.wordcloudLoading = true;
    }
    this.updateFilteredContent();
    this.buildChartOptions();
    this.renderMermaidDiagram();
  }

  onWordcloudLoad() {
    this.wordcloudLoading = false;
  }

  private updateFilteredContent() {
    const typology = this.question?.typology;
    if (this.surveyService.isOpenedQuestion(typology)) {
      const q = this.currentSurvey?.survey_question;
      const responses = q?.responses || [];
      const text = responses.map((r: any) => r.answer).join(' ');
      this.filteredContent = this.surveyService.getCleanSentence(text);
    } else {
      this.filteredContent = '';
    }
  }

  generateMermaidDiagram(): string {
    const surveyMap = new Map<string, any>();
    this.surveys.forEach(s => surveyMap.set(`${s.major_version}.${s.minor_version}`, s));

    const nodes: string[] = [];
    const links: string[] = [];

    this.surveys.forEach((s, idx) => {
      const nodeId = `S_${s.id}`;
      const label = `Session ${s.version_number}`;

      let styledNode = `${nodeId}("${label}"):::started`;

      if (s.id === this.currentSurvey?.id) {
        styledNode = `${nodeId}("${label}"):::current`;
      }

      nodes.push(styledNode);

      if (idx > 0) {
        const prev = this.surveys[idx - 1];
        links.push(`S_${prev.id} --> S_${s.id}`);
      }
    });

    // Stili CSS Mermaid aggiornati al viola
    const styleBlock = [
      'classDef started fill:#f4f0fc,stroke:#9169db,stroke-width:1px,color:#5e4391;',
      'classDef current fill:#9169db,stroke:#9169db,stroke-width:2px,color:#fff;'
    ].join('\n');

    return ['flowchart LR', ...nodes, ...links, styleBlock].join('\n');
  }

  get timelineIsDisabled(): boolean {
    return this.availableChartTypes.length === 0;
  }

  onChartClick(event: any) {
    const question = this.questionHistory?.questions[event?.dataIndex];
    if (question) {
      this.dialog.open(SingleQuestionResponsesComponent, {
        panelClass: 'fullscreen-dialog',
        data: {
          questionID: question.id,
          showPrivateInfo: true,
          isDialog: true
        }
      });
    }
  }

  setSmooth(smooth: boolean) {
    this.isSmooth = smooth;
    this.buildChartOptions();
  }

  // --- UI Helpers for Timeline Evolution ---

  getDecisionLabel(decision: string): string {
    switch (decision) {
      case 'ACCEPTED': return 'Accepted';
      case 'REJECTED': return 'Rejected';
      case 'REVISED': return 'Revised';
      case 'REVISED_SPLIT': return 'Split & Revised';
      case 'REPROPOSED': return 'Reproposed';
      default: return decision || 'Unknown';
    }
  }

  getDecisionBadgeClass(decision: string): string {
    switch (decision) {
      case 'ACCEPTED': return 'badge badge-success';
      case 'REJECTED': return 'badge badge-danger';
      case 'REVISED': return 'badge badge-primary-purple';
      case 'REVISED_SPLIT': return 'badge badge-info';
      case 'REPROPOSED': return 'badge badge-secondary';
      default: return 'badge badge-light';
    }
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  formatChangeCategory(category: string): string {
    const map: { [key: string]: string } = {
      'general': 'General Fields',
      'customs': 'Custom Configs',
      'choices': 'Choices / Options',
      'grid': 'Grid Rows & Columns',
      'attachments': 'Attachments'
    };
    return map[category] || category;
  }
}
